import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import * as WebBrowser from "expo-web-browser";
import Container from "@/components/shared/AppContainer";
import Text from "@/components/shared/AppText";
import { useAuth } from "../../AuthContext";
import AppTitle from "../../shared/AppTitle";
import OnboardingButton from "../../shared/OnboardingButton";
import { useBuy } from "../../BuyContext";
import { formatPriceWithCurrency } from "../../globalUtils";
import tw from "twrnc";
import { Ionicons } from "@expo/vector-icons";
import SupportButton from "@/components/support/SupportButton";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import AppConfig from "../../../AppConfig";
import { t, tn } from "@/localization";

const environment = process.env.NODE_ENV;
const LEGACY_BUSINESS_PRICING_CUTOFF = "2026-03-01";

const unlimitedPlanOptions = [
  {
    id: "monthly",
    title: t("Monthly"),
    productId: "subscription_prices_monthly",
    billingCycle: t("Billed monthly"),
    valueSuffix: t("/mo"),
    url_test: "https://buy.stripe.com/test_6oEbIZg5E1FB4Ao9AC",
    url_prod: "https://buy.stripe.com/00w14mbAE4Dw5tCcGvew80h",
  },
  {
    id: "annual",
    title: t("Annual"),
    productId: "subscription_prices_annual",
    billingCycle: t("Billed annually"),
    valueSuffix: t("/yr"),
    url_test: "https://buy.stripe.com/test_dR63ct8Dcdojc2Q6op",
    url_prod: "https://buy.stripe.com/5kQ6oGeMQ8TMf4c49Zew80g",
  },
];

const ToggleButton = ({ active, onPress, icon, label }) => (
  <Pressable
    onPress={onPress}
    style={[
      tw`flex-1 py-2.5 px-3 rounded-lg flex-row items-center justify-center`,
      active ? tw`bg-[#3F5B7A]` : tw`bg-transparent`,
    ]}
  >
    <Ionicons
      name={icon}
      size={18}
      color={active ? "white" : "#3F5B7A"}
      style={tw`mr-1.5`}
    />
    <Text
      style={[
        tw`font-bold text-sm`,
        active ? tw`text-white` : tw`text-[#3F5B7A]`,
      ]}
    >
      {label}
    </Text>
  </Pressable>
);

const SelectableRow = ({
  selected,
  onPress,
  title,
  subtitle,
  value,
  valueNote,
  valueNoteStyle,
}) => (
  <Pressable
    onPress={onPress}
    style={[
      tw`rounded-xl border p-3 mb-2.5`,
      selected
        ? tw`border-[#F4553B] bg-[#FFF5F3]`
        : tw`border-gray-200 bg-white`,
    ]}
  >
    <View style={tw`flex-row items-start justify-between`}>
      <View style={tw`flex-1 pr-3`}>
        <Text style={tw`text-base font-bold text-[#253447]`}>{title}</Text>
        {!!subtitle && (
          <Text style={tw`text-xs text-gray-500 mt-1`}>{subtitle}</Text>
        )}
      </View>
      <View style={tw`items-end pr-6`}>
        <Text style={tw`text-base font-extrabold text-[#253447]`}>{value}</Text>
        {!!valueNote && (
          <Text style={[tw`text-xs text-gray-500 mt-1`, valueNoteStyle]}>
            {valueNote}
          </Text>
        )}
      </View>
    </View>
    <View style={tw`absolute right-3 pt-[2px] top-3`}>
      <Ionicons
        name={selected ? "checkmark-circle" : "ellipse-outline"}
        size={18}
        color={selected ? "#F4553B" : "#C4C7CE"}
      />
    </View>
  </Pressable>
);

const Plans = ({ navigation, route }) => {
  const isMobileUX = useIsMobileUX();
  const [submitting, setSubmitting] = useState(false);
  // ?tab=unlimited (email links) opens the subscription tab.
  const [activeTab, setActiveTab] = useState(
    route?.params?.tab === "unlimited" ? "unlimited" : "credits"
  );
  const [selectedCreditId, setSelectedCreditId] = useState("single");
  const [selectedUnlimitedId, setSelectedUnlimitedId] = useState("annual");
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (event) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setIsScrolled(offsetY > 20);
  };

  const { broadcaster, email, account_type, registered_datetime } = useAuth();
  const {
    purchase,
    getCurrencyPrice,
    getPricePerItem,
    getOriginalPrice,
    getProductCurrency,
    pricingReady,
  } = useBuy();

  const isLegacyBusinessCustomer = useMemo(() => {
    if (account_type !== "business") return false;
    if (typeof registered_datetime !== "string") return false;

    // Compare yyyy-mm-dd prefix to avoid timezone shifts in display logic.
    return registered_datetime.slice(0, 10) < LEGACY_BUSINESS_PRICING_CUTOFF;
  }, [account_type, registered_datetime]);

  const fromEventPaywall =
    route?.params?.source === "event_paywall" || Boolean(route?.params?.slug);
  const eventSlug = route?.params?.slug || route?.params?.event?.slug;
  const eventTitle = route?.params?.event?.title;

  const eventDays = Number(route?.params?.event?.days) || 1;
  const eventDaysPaid = Number(route?.params?.event?.days_paid) || 0;
  const eventIsPublished = route?.params?.event?.is_published;
  const creditsNeeded = fromEventPaywall
    ? Math.max(eventIsPublished ? 0 : eventDays - eventDaysPaid, 1)
    : 1;

  const creditOptions = useMemo(
    () => [
      {
        id: "single",
        qty: creditsNeeded,
        productId: isLegacyBusinessCustomer
          ? "specialevent50"
          : "single_business_new",
        title: fromEventPaywall
          ? creditsNeeded > 1
            ? t("Unlock {count}-day event", { count: creditsNeeded })
            : t("Unlock single event")
          : t("Single credit"),
        subtitle: fromEventPaywall
          ? tn(
              "Use {count} credit to unlock this event now",
              "Use {count} credits to unlock this event now",
              creditsNeeded
            )
          : t("One event"),
      },
      {
        id: "pack_5",
        qty: 5,
        productId: isLegacyBusinessCustomer ? "package_5" : "package_5_new",
        title: tn("{count} event", "{count} events", 5),
        // subtitle: "For recurring events",
      },
      {
        id: "pack_10",
        qty: 10,
        productId: isLegacyBusinessCustomer ? "package_10" : "package_10_new",
        title: tn("{count} event", "{count} events", 10),
        // subtitle: "More value for frequent use",
      },
      {
        id: "pack_25",
        qty: 25,
        productId: isLegacyBusinessCustomer ? "package_25" : "package_25_new",
        title: tn("{count} event", "{count} events", 25),
        // subtitle: "Best for busy teams",
      },
      {
        id: "pack_50",
        qty: 50,
        productId: isLegacyBusinessCustomer ? "package_50" : "package_50_new",
        title: tn("{count} event", "{count} events", 50),
        // subtitle: "Highest volume package",
      },
    ],
    [isLegacyBusinessCustomer, fromEventPaywall, creditsNeeded]
  );

  const selectedCredit =
    creditOptions.find((option) => option.id === selectedCreditId) ||
    creditOptions[0];
  const selectedUnlimited =
    unlimitedPlanOptions.find((option) => option.id === selectedUnlimitedId) ||
    unlimitedPlanOptions[0];

  const getUnlimitedDisplayPrice = useCallback(
    (plan) => {
      if (!pricingReady) return "";
      if (!plan?.productId) return "";
      const price = getCurrencyPrice(plan.productId);
      if (!price) return "";
      return `${price}${plan.valueSuffix || ""}`;
    },
    [getCurrencyPrice, pricingReady]
  );

  const annualSavingsText = useMemo(() => {
    if (!pricingReady) return "";

    const monthlyPrice = getOriginalPrice("subscription_prices_monthly");
    const annualPrice = getOriginalPrice("subscription_prices_annual");

    if (
      !Number.isFinite(monthlyPrice) ||
      !Number.isFinite(annualPrice) ||
      monthlyPrice <= 0 ||
      annualPrice <= 0
    ) {
      return "";
    }

    const savings = monthlyPrice * 12 - annualPrice;
    if (!Number.isFinite(savings) || savings <= 0) {
      return "";
    }

    const currency =
      getProductCurrency("subscription_prices_annual") ||
      getProductCurrency("subscription_prices_monthly") ||
      "USD";

    return t("Save {amount}", {
      amount: formatPriceWithCurrency(savings, currency),
    });
  }, [getOriginalPrice, getProductCurrency, pricingReady]);

  const ctaText = useMemo(() => {
    if (activeTab === "unlimited") return t("Subscribe");

    if (selectedCredit?.id === "single") {
      if (fromEventPaywall) {
        return tn(
          "Unlock with {count} Credit",
          "Unlock with {count} Credits",
          creditsNeeded
        );
      }
      return t("Buy Single Credit");
    }

    if (fromEventPaywall && selectedCredit?.qty >= creditsNeeded) {
      return t("Buy {count} Credits & Unlock Event", {
        count: selectedCredit?.qty || "",
      });
    }

    return t("Buy {count} Event Credits", { count: selectedCredit?.qty || "" });
  }, [activeTab, selectedCredit, fromEventPaywall, creditsNeeded]);

  const selectedSummary = useMemo(() => {
    if (activeTab === "unlimited") {
      return `${selectedUnlimited.title}: ${getUnlimitedDisplayPrice(
        selectedUnlimited
      )}`;
    }

    if (!pricingReady) {
      return t("{plan}: loading", { plan: selectedCredit.title });
    }

    const price = getCurrencyPrice(selectedCredit.productId);
    return `${selectedCredit.title}: ${price}`;
  }, [
    activeTab,
    selectedUnlimited,
    selectedCredit,
    getCurrencyPrice,
    getUnlimitedDisplayPrice,
    pricingReady,
  ]);

  const handleSubscribe = async () => {
    try {
      setSubmitting(true);
      const selected = selectedUnlimited;
      const baseUrl =
        environment === "production" ? selected.url_prod : selected.url_test;
      const url =
        `${baseUrl}?prefilled_email=${encodeURIComponent(email || "")}` +
        `&client_reference_id=${encodeURIComponent(broadcaster || "")}`;

      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!pricingReady) {
      return;
    }

    if (activeTab === "unlimited") {
      await handleSubscribe();
      return;
    }

    const shouldUnlockEvent =
      fromEventPaywall &&
      Boolean(eventSlug) &&
      selectedCredit.qty >= creditsNeeded;
    const purchaseQuantity = shouldUnlockEvent
      ? creditsNeeded
      : selectedCredit.id === "single"
      ? selectedCredit.qty
      : 1;

    await purchase({
      productId: selectedCredit.productId,
      slug: shouldUnlockEvent ? eventSlug : undefined,
      title: shouldUnlockEvent ? eventTitle : undefined,
      quantity: purchaseQuantity,
    });
  };

  const openPolicyLink = async (url) => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank");
      return;
    }

    await WebBrowser.openBrowserAsync(url);
  };

  const openTerms = () => openPolicyLink(AppConfig.TERMS_URL);
  const openPrivacy = () => openPolicyLink(AppConfig.PRIVACY_URL);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    if (window.Intercom) {
      window.Intercom("update", { hide_default_launcher: true });
    }
    return () => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: false });
      }
    };
  }, []);

  const close = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.navigate("Profile");
  };

  const storageBenefitText =
    activeTab === "unlimited"
      ? t("Unlimited storage while you're subscribed")
      : t("Unlimited storage as long as you make one pack purchase per year");

  return (
    <Container>
      {submitting && (
        <View
          style={tw`absolute inset-0 items-center justify-center bg-white/40 z-10`}
        >
          <ActivityIndicator />
        </View>
      )}

      <View style={tw`flex-1 w-full max-w-[640px] self-center`}>
        {!isScrolled && (
          <Pressable
            onPress={close}
            style={tw`absolute top-1 left-1 z-20 p-1`}
            hitSlop={8}
          >
            <Ionicons name="close-outline" size={30} color="#B0B0B0" />
          </Pressable>
        )}
        {!isScrolled && (Platform.OS !== "web" || isMobileUX) && (
          <SupportButton style={tw`absolute top-2 right-3 z-20 p-1`} />
        )}

        <ScrollView
          style={tw`flex-1`}
          contentContainerStyle={tw`w-full px-4 pt-1 pb-[220px]`}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          nativeID="plans-scroll"
        >
          <View style={tw`w-full self-center`}>
            <AppTitle
              text={t("EventLive Plans")}
              style={tw`text-center my-4`}
            />

            <View style={tw`rounded-xl bg-gray-100 p-1 mb-5 flex-row`}>
              <ToggleButton
                active={activeTab === "credits"}
                onPress={() => setActiveTab("credits")}
                icon="wallet-outline"
                label={t("Credits")}
              />
              <ToggleButton
                active={activeTab === "unlimited"}
                onPress={() => setActiveTab("unlimited")}
                icon="infinite-outline"
                label={t("Unlimited")}
              />
            </View>

            {activeTab === "credits" && (
              <View>
                {isLegacyBusinessCustomer && (
                  <View style={tw`rounded-lg mb-3`}>
                    <Text style={tw`text-sm text-green-700 text-center`}>
                      {t(
                        "You see grandfathered (legacy) pricing. Prices for new clients may vary."
                      )}
                    </Text>
                  </View>
                )}
                {creditOptions.map((option) => (
                  <SelectableRow
                    key={option.id}
                    selected={selectedCreditId === option.id}
                    onPress={() => setSelectedCreditId(option.id)}
                    title={option.title}
                    subtitle={option.subtitle}
                    value={
                      pricingReady
                        ? getCurrencyPrice(
                            option.productId,
                            option.id === "single" ? creditsNeeded : 1
                          )
                        : "--"
                    }
                    valueNote={(() => {
                      if (!pricingReady) return "";
                      if (option.qty <= 1) return "";
                      const pricePerItem = getPricePerItem(option.productId);
                      if (!pricePerItem) return "";
                      return option.id === "single"
                        ? t("{price} per day", { price: pricePerItem })
                        : t("{price} per event", { price: pricePerItem });
                    })()}
                  />
                ))}
              </View>
            )}

            {activeTab === "unlimited" && (
              <View>
                {unlimitedPlanOptions.map((plan) => (
                  <SelectableRow
                    key={plan.id}
                    selected={selectedUnlimitedId === plan.id}
                    onPress={() => setSelectedUnlimitedId(plan.id)}
                    title={plan.title}
                    subtitle={plan.billingCycle}
                    value={getUnlimitedDisplayPrice(plan)}
                    valueNote={plan.id === "annual" ? annualSavingsText : ""}
                    valueNoteStyle={
                      plan.id === "annual" ? tw`text-green-700` : null
                    }
                  />
                ))}
              </View>
            )}

            <View style={tw`mt-3 p-3`}>
              <Text style={tw`text-base font-bold text-[#253447] mb-2`}>
                {t("Every Event Includes")}
              </Text>
              <View style={tw`gap-y-1.5`}>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="people-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("Unlimited viewers")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="videocam-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("Full-day HD broadcasting")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="color-palette-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("White-labeling and website embedding")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="chatbubble-ellipses-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("Unlimited email and chat support")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="analytics-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("Professional Analytics")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="speedometer-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{t("5000 viewer-hours per event (soft limit)")}</Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="ticket-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>
                    {t(
                      "Unlimited pay-per-view viewer-hours ($1.50 per ticket sold)"
                    )}
                  </Text>
                </View>
                <View style={tw`flex-row items-center`}>
                  <Ionicons
                    name="folder-open-outline"
                    size={18}
                    color="#3F5B7A"
                    style={tw`mr-2`}
                  />
                  <Text>{storageBenefitText}</Text>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={tw`border-t border-gray-200 bg-white px-4 pt-3 pb-2`}>
          {Platform.OS === "web" && (
            <View
              pointerEvents="none"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: -18,
                height: 18,
                backgroundImage:
                  "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
              }}
            />
          )}
          <View style={tw`w-full self-center`}>
            {/* <Text style={tw`text-center text-xs text-gray-500 mb-2`}>
              {selectedSummary}
            </Text> */}
            <OnboardingButton
              onPress={handlePrimaryAction}
              text={pricingReady ? ctaText : t("Loading Prices")}
              loading={submitting || !pricingReady}
              disabled={submitting || !pricingReady}
              style={tw`bg-[#f4553b] mb-0`}
              icon={Platform.OS === "web" ? "card-outline" : null}
            />

            <View
              style={tw`mt-1 flex-row justify-between items-center gap-3 px-1`}
            >
              <Pressable onPress={openTerms}>
                <Text style={tw`text-xs text-[#3F5B7A] underline`}>
                  {t("Terms")}
                </Text>
              </Pressable>
              {/* Credits never expire; a subscription is cancelled from
                  Profile › View Invoices and Manage Subscription. Shrinks and
                  wraps so longer languages keep both links on screen. */}
              <View style={tw`mt-2 p-2 flex-1`}>
                <View style={tw`flex-row items-center justify-center`}>
                  {/* <Ionicons
                    name="shield-checkmark"
                    size={14}
                    style={tw`text-green-700 mr-1`}
                  /> */}
                  <Text style={tw`text-xs text-green-700 font-bold`}>
                    {activeTab === "unlimited"
                      ? t("Cancel anytime")
                      : t("Event credits do not expire")}
                  </Text>
                </View>
                <Text style={tw`text-xs text-green-700 text-center mt-0.5`}>
                  {activeTab === "unlimited"
                    ? t("Manage it in your Profile")
                    : t("Reschedule or cancel free")}
                </Text>
              </View>
              <Pressable onPress={openPrivacy}>
                <Text style={tw`text-xs text-[#3F5B7A] underline`}>
                  {t("Privacy")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Container>
  );
};

export default Plans;
